import React, { useState, useEffect } from 'react';
import { getRecipeById, updateRecipe } from '../services/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageHeading, Heading, Text, Modal } from '../components/ui';
import { useAuth } from '../context/authContext';
import TimerIcon from '../components/ui/TimerIcon';
import SignedImage from '../components/SignedImage';
import RecipeComponentEditor, { AssemblyEditor } from '../components/RecipeComponentEditor';
import Toast from '../components/Toast';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    notecardBlue: '#4A90E2', // Blue color for notecard dividing lines
    red: '#DC2626', // Red color for title separator
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// TimerEditModal component - Now handles active/inactive timers
function TimerEditModal({ open, onClose, onSave, onRemove, stepIndex, timingSegments = [] }) {
    // Parse timing segments if they're a string
    let segments = timingSegments;
    if (typeof timingSegments === 'string') {
        try {
            segments = JSON.parse(timingSegments);
        } catch (e) {
            segments = [];
        }
    }
    
    // Ensure segments is always an array
    if (!Array.isArray(segments)) {
        segments = [];
    }

    // Ensure we have both active and inactive
    const activeSegment = segments.find(s => s.kind === 'active') || {
        kind: 'active',
        label: 'Active time',
        expectedSeconds: 0
    };
    const inactiveSegment = segments.find(s => s.kind === 'inactive') || {
        kind: 'inactive',
        label: 'Inactive time',
        expectedSeconds: 0
    };

    const [activeMinutes, setActiveMinutes] = React.useState(Math.floor(activeSegment.expectedSeconds / 60));
    const [activeSeconds, setActiveSeconds] = React.useState(activeSegment.expectedSeconds % 60);
    const [activeLabel, setActiveLabel] = React.useState(activeSegment.label || 'Active time');
    
    const [inactiveMinutes, setInactiveMinutes] = React.useState(Math.floor(inactiveSegment.expectedSeconds / 60));
    const [inactiveSeconds, setInactiveSeconds] = React.useState(inactiveSegment.expectedSeconds % 60);
    const [inactiveLabel, setInactiveLabel] = React.useState(inactiveSegment.label || 'Inactive time');

    React.useEffect(() => {
        if (open) {
            // Re-parse segments in case timingSegments changed
            let currentSegments = timingSegments;
            if (typeof timingSegments === 'string') {
                try {
                    currentSegments = JSON.parse(timingSegments);
                } catch (e) {
                    currentSegments = [];
                }
            }
            if (!Array.isArray(currentSegments)) {
                currentSegments = [];
            }
            
            const active = currentSegments.find(s => s.kind === 'active') || { expectedSeconds: 0, label: 'Active time' };
            const inactive = currentSegments.find(s => s.kind === 'inactive') || { expectedSeconds: 0, label: 'Inactive time' };
            
            setActiveMinutes(Math.floor(active.expectedSeconds / 60));
            setActiveSeconds(active.expectedSeconds % 60);
            setActiveLabel(active.label || 'Active time');
            
            setInactiveMinutes(Math.floor(inactive.expectedSeconds / 60));
            setInactiveSeconds(inactive.expectedSeconds % 60);
            setInactiveLabel(inactive.label || 'Inactive time');
        }
    }, [open, timingSegments]);

    const handleSave = () => {
        const activeTotalSeconds = activeMinutes * 60 + activeSeconds;
        const inactiveTotalSeconds = inactiveMinutes * 60 + inactiveSeconds;
        
        const newSegments = [
            {
                kind: 'active',
                label: activeLabel,
                expectedSeconds: activeTotalSeconds,
                order: 0
            },
            {
                kind: 'inactive',
                label: inactiveLabel,
                expectedSeconds: inactiveTotalSeconds,
                order: 1
            }
        ];
        
        onSave(newSegments);
    };

    if (!open) return null;
    
    const totalMinutes = activeMinutes + inactiveMinutes;
    const totalSeconds = activeSeconds + inactiveSeconds;
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px] max-w-[90vw]">
                <h2 className="font-mono text-lg mb-4">Edit Timers for Step {stepIndex + 1}</h2>
                
                {/* Active Timer (Red) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#991b1b' }}>ACTIVE TIME</label>
                    <input
                        type="text"
                        value={activeLabel}
                        onChange={e => setActiveLabel(e.target.value)}
                        placeholder="e.g., Whisking to stiff peaks"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={activeMinutes}
                                onChange={e => setActiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={activeSeconds}
                                onChange={e => setActiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Inactive Timer (Blue) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#1e40af' }}>INACTIVE TIME</label>
                    <input
                        type="text"
                        value={inactiveLabel}
                        onChange={e => setInactiveLabel(e.target.value)}
                        placeholder="e.g., Baking until golden brown"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={inactiveMinutes}
                                onChange={e => setInactiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={inactiveSeconds}
                                onChange={e => setInactiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Total Duration Display */}
                <div className="mb-4 p-2 bg-neutral-50 rounded text-center">
                    <span className="font-mono text-xs text-neutral-600">Total: {totalMinutes}m {totalSeconds}s</span>
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        className="px-4 py-2 bg-neutral-200 text-neutral-900 rounded font-mono text-xs hover:bg-neutral-300"
                        onClick={onClose}
                    >Cancel</button>
                    {onRemove && (
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded font-mono text-xs hover:bg-red-700"
                            onClick={onRemove}
                        >Remove</button>
                    )}
                    <button
                        className="px-4 py-2 bg-neutral-900 text-white rounded font-mono text-xs hover:bg-neutral-800"
                        onClick={handleSave}
                    >Save</button>
                </div>
            </div>
        </div>
    );
}

// Image resizing utility function
const resizeImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = img;
            
            if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
            }
            
            if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
            }
            
            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;
            
            // Draw resized image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob
            canvas.toBlob((blob) => {
                // Create a new file with the resized image
                const resizedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                });
                resolve(resizedFile);
            }, file.type, quality);
        };
        
        img.src = URL.createObjectURL(file);
    });
};

const EditRecipe = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Add timerAssociations to formData state
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        cooking_time: '',
        // Multi-component flag
        isMultiComponent: false,
        // Simple mode fields
        ingredients: [''],
        prerequisites: [''],
        steps: [''],
        // Multi-component mode fields
        components: [],
        assembly: { prerequisites: [''], steps: [''] },
        // Other fields
        images: [],
        timerAssociations: [],
    });
    const [existingImages, setExistingImages] = useState([]);
    const [error, setError] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [editingField, setEditingField] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [dragOver, setDragOver] = useState(false);

    // Timer Association Handlers (copied from CreateRecipe.jsx)
    const addTimerAssociation = () => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: [
                ...prev.timerAssociations,
                { stepIndex: 0, duration: 0, ingredients: [''] }
            ]
        }));
    };
    const updateTimerAssociation = (idx, field, value) => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: prev.timerAssociations.map((ta, i) =>
                i === idx ? { ...ta, [field]: value } : ta
            )
        }));
    };
    const removeTimerAssociation = (idx) => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: prev.timerAssociations.filter((_, i) => i !== idx)
        }));
    };
    const [timerModal, setTimerModal] = useState({ open: false, timerIndex: null, stepIndex: null, timingSegments: null });
    const [stepTimingSegments, setStepTimingSegments] = useState({}); // Store timing_segments for each step

    // Multi-component handlers
    const toggleMultiComponent = () => {
        setFormData(prev => ({
            ...prev,
            isMultiComponent: !prev.isMultiComponent,
            components: !prev.isMultiComponent && prev.components.length === 0 
                ? [{ 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }] 
                : prev.components
        }));
    };

    const addComponent = () => {
        setFormData(prev => ({
            ...prev,
            components: [
                ...prev.components,
                { 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }
            ]
        }));
    };

    const updateComponent = (index, updatedComponent) => {
        setFormData(prev => ({
            ...prev,
            components: prev.components.map((comp, i) => 
                i === index ? { ...comp, ...updatedComponent } : comp
            )
        }));
    };

    const removeComponent = (index) => {
        if (formData.components.length > 1) {
            setFormData(prev => ({
                ...prev,
                components: prev.components.filter((_, i) => i !== index)
            }));
        }
    };

    const updateAssembly = (newAssembly) => {
        setFormData(prev => ({
            ...prev,
            assembly: newAssembly
        }));
    };

    const categories = [
        'Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Snack', 
        'Dessert', 'Supper', 'Appetizer', 'Side', 'Drink', 
        'Condiment', 'Bread', 'Salad', 'Soup'
    ];

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const data = await getRecipeById(id);
                
                // Parse timer associations safely
                let timerAssociations = [];
                if (data.timer_associations) {
                    try {
                        timerAssociations = typeof data.timer_associations === 'string' 
                            ? JSON.parse(data.timer_associations) 
                            : data.timer_associations;
                    } catch (parseError) {
                        console.error('Error parsing timer associations:', parseError);
                        timerAssociations = [];
                    }
                }
                
                const steps = data.instructions && data.instructions.steps && data.instructions.steps.length > 0
                    ? data.instructions.steps
                    : [''];
                
                // Load timing_segments from steps_with_timing if available (preferred method)
                // Otherwise, fall back to generating from timer_associations
                const initialTimingSegments = {};
                
                if (data.steps_with_timing && Array.isArray(data.steps_with_timing) && data.steps_with_timing.length > 0) {
                    // Use timing_segments from steps_with_timing (preserves active/inactive timers)
                    data.steps_with_timing.forEach((stepWithTiming, index) => {
                        if (stepWithTiming.timing_segments && Array.isArray(stepWithTiming.timing_segments)) {
                            // Ensure we have both active and inactive timers
                            const segments = stepWithTiming.timing_segments;
                            const hasActive = segments.some(s => s.kind === 'active');
                            const hasInactive = segments.some(s => s.kind === 'inactive');
                            
                            if (hasActive && hasInactive) {
                                initialTimingSegments[index] = segments;
                            } else {
                                // Normalize to ensure both exist
                                const active = segments.find(s => s.kind === 'active') || {
                                    kind: 'active',
                                    label: 'Active time',
                                    expectedSeconds: 0,
                                    order: 0
                                };
                                const inactive = segments.find(s => s.kind === 'inactive') || {
                                    kind: 'inactive',
                                    label: 'No waiting required',
                                    expectedSeconds: 0,
                                    order: 1
                                };
                                initialTimingSegments[index] = [active, inactive];
                            }
                        }
                    });
                } else {
                    // Fallback: Convert timer_associations to timing_segments format
                    steps.forEach((_, index) => {
                        const ta = timerAssociations.find(t => t.stepIndex === index);
                        if (ta && ta.duration) {
                            // Convert duration (in minutes) to seconds and create timing_segments
                            const durationSeconds = ta.duration * 60;
                            initialTimingSegments[index] = [
                                {
                                    kind: 'active',
                                    label: ta.description || `Step ${index + 1} active time`,
                                    expectedSeconds: durationSeconds,
                                    order: 0
                                },
                                {
                                    kind: 'inactive',
                                    label: 'No waiting required',
                                    expectedSeconds: 0,
                                    order: 1
                                }
                            ];
                        }
                    });
                }
                setStepTimingSegments(initialTimingSegments);
                
                // Check if this is a multi-component recipe
                const isMultiComponent = data.isMultiComponent === true;
                
                // Parse components if available
                let components = [];
                if (data.components) {
                    try {
                        components = typeof data.components === 'string' 
                            ? JSON.parse(data.components) 
                            : data.components;
                    } catch (e) {
                        console.error('Error parsing components:', e);
                        components = [];
                    }
                }
                
                // Parse assembly if available
                let assembly = { prerequisites: [''], steps: [''] };
                if (data.assembly) {
                    try {
                        assembly = typeof data.assembly === 'string' 
                            ? JSON.parse(data.assembly) 
                            : data.assembly;
                        // Ensure arrays exist
                        assembly.prerequisites = assembly.prerequisites || [''];
                        assembly.steps = assembly.steps || [''];
                    } catch (e) {
                        console.error('Error parsing assembly:', e);
                    }
                }
                
                setFormData({
                    name: data.name || '',
                    category: data.category || '',
                    cooking_time: data.cooking_time !== undefined ? data.cooking_time : '',
                    isMultiComponent: isMultiComponent,
                    ingredients: Array.isArray(data.ingredients) && data.ingredients.length > 0 
                        ? data.ingredients 
                        : [''],
                    prerequisites: data.instructions && data.instructions.prerequisites && data.instructions.prerequisites.length > 0
                        ? data.instructions.prerequisites
                        : [''],
                    steps: steps,
                    components: Array.isArray(components) && components.length > 0 
                        ? components 
                        : [],
                    assembly: assembly,
                    images: [], // New images will be added here.
                    timerAssociations: Array.isArray(timerAssociations) ? timerAssociations : []
                });
                setExistingImages(data.images || []);
            } catch (err) {
                console.error('Error fetching recipe:', err);
                setError('Failed to load recipe. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchRecipe();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'images') {
            handleImageUpload(files);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleImageUpload = async (files) => {
        setUploadError('');
        const fileArray = Array.from(files);
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const processedFiles = [];
        const newPreviews = [];

        try {
            for (let i = 0; i < fileArray.length; i++) {
                const file = fileArray[i];
                
                // Check file type
                if (!file.type.startsWith('image/')) {
                    setUploadError(`File "${file.name}" is not an image.`);
                    return;
                }

                // Check file size and resize if necessary
                let processedFile = file;
                if (file.size > maxFileSize) {
                    try {
                        processedFile = await resizeImage(file);
                        console.log(`Resized ${file.name} from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
                    } catch (resizeError) {
                        console.error('Error resizing image:', resizeError);
                        setUploadError(`Failed to resize ${file.name}. Please try a smaller image.`);
                        return;
                    }
                }

                processedFiles.push(processedFile);

                // Create preview
                const reader = new FileReader();
                reader.onload = (e) => {
                    newPreviews.push(e.target.result);
                    if (newPreviews.length === fileArray.length) {
                        setImagePreviews(prev => [...prev, ...newPreviews]);
                    }
                };
                reader.readAsDataURL(processedFile);
            }

            setFormData(prev => ({ ...prev, images: [...prev.images, ...processedFiles] }));
        } catch (error) {
            console.error('Error processing images:', error);
            setUploadError('Failed to process images. Please try again.');
        }
    };

    const removeNewImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    // Drag and drop handlers (matching CreateRecipe)
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        handleImageUpload(files);
    };

    const handleFileInput = (e) => {
        const files = Array.from(e.target.files);
        handleImageUpload(files);
        e.target.value = ''; // Clear input to allow re-uploading same file
    };

    const triggerFileInput = () => {
        const input = document.getElementById('edit-image-input');
        if (input) {
            input.click();
        }
    };

    const handleListChange = (listName, index, value) => {
        setFormData(prev => ({
            ...prev,
            [listName]: prev[listName].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (listName) => {
        setFormData(prev => ({
            ...prev,
            [listName]: [...prev[listName], '']
        }));
    };

    const removeListItem = (listName, index) => {
        // Allow deletion of prerequisites even when there's only one
        if (listName === 'prerequisites' || formData[listName].length > 1) {
            setFormData(prev => ({
                ...prev,
                [listName]: prev[listName].filter((_, i) => i !== index)
            }));
        }
    };

    const handleRemoveExistingImage = (imgUrl) => {
        setExistingImages((prev) => prev.filter((img) => img !== imgUrl));
    };

    const nextImage = () => {
        const allImages = [...existingImages, ...imagePreviews];
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
        }
    };

    const prevImage = () => {
        const allImages = [...existingImages, ...imagePreviews];
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
        }
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    const startEditing = (field) => {
        setEditingField(field);
    };

    const stopEditing = () => {
        setEditingField(null);
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setUploadError('');
        setSubmitting(true);

        const {
            name = '',
            category = '',
            cooking_time = '',
            isMultiComponent = false,
            ingredients = [],
            prerequisites = [],
            steps = [],
            components = [],
            assembly = { prerequisites: [], steps: [] }
        } = formData;

        // Basic validation
        if (!name.trim() || !category.trim() || cooking_time === '') {
            setError('Please fill out name, category, and cooking time.');
            setSubmitting(false);
            return;
        }

        // Validation differs based on isMultiComponent flag
        if (isMultiComponent) {
            // Multi-component validation
            if (!components || components.length === 0) {
                setError('Please add at least one component.');
                setSubmitting(false);
                return;
            }
            
            for (let i = 0; i < components.length; i++) {
                const comp = components[i];
                if (!comp.name || !comp.name.trim()) {
                    setError(`Component ${i + 1} needs a name.`);
                    setSubmitting(false);
                    return;
                }
                const compIngredients = (comp.ingredients || []).filter(ing => ing && ing.trim());
                if (compIngredients.length === 0) {
                    setError(`Component "${comp.name}" needs at least one ingredient.`);
                    setSubmitting(false);
                    return;
                }
                const compSteps = (comp.steps || []).filter(step => {
                    if (typeof step === 'string') return step.trim();
                    if (step && step.text) return step.text.trim();
                    return false;
                });
                if (compSteps.length === 0) {
                    setError(`Component "${comp.name}" needs at least one step.`);
                    setSubmitting(false);
                    return;
                }
            }
            
            const assemblySteps = (assembly?.steps || []).filter(step => {
                if (typeof step === 'string') return step.trim();
                if (step && step.text) return step.text.trim();
                return false;
            });
            if (assemblySteps.length === 0) {
                setError('Please add at least one assembly step to combine the components.');
                setSubmitting(false);
                return;
            }
        } else {
            // Simple recipe validation
        const validIngredients = ingredients.filter(item => item.trim());
        const validSteps = steps.filter(item => item.trim());

            if (validIngredients.length === 0 || validSteps.length === 0) {
                setError('Please add at least one ingredient and one cooking step.');
            setSubmitting(false);
            return;
            }
        }

        try {
            if (!user) throw new Error('User not authenticated');
            const token = await user.getIdToken();

            const submitData = new FormData();
            submitData.append('name', name.trim());
            submitData.append('category', category.trim());
            submitData.append('cooking_time', cooking_time);
            submitData.append('isMultiComponent', isMultiComponent);

            if (isMultiComponent) {
                // Multi-component submission
                submitData.append('components', JSON.stringify(components));
                submitData.append('assembly', JSON.stringify(assembly));
                submitData.append('instructions', JSON.stringify({ prerequisites: [], steps: [] }));
                submitData.append('ingredients', JSON.stringify([]));
            } else {
                // Simple recipe submission
                const validIngredients = ingredients.filter(item => item.trim());
                const validPrerequisites = prerequisites.filter(item => item.trim());
                const validSteps = steps.filter(item => item.trim());

                submitData.append('ingredients', JSON.stringify(validIngredients));

            const instructions = {
                prerequisites: validPrerequisites,
                steps: validSteps
            };
            submitData.append('instructions', JSON.stringify(instructions));

            submitData.append('timer_associations', JSON.stringify(formData.timerAssociations));
            
            const stepsWithTiming = validSteps.map((stepText, index) => {
                const segments = stepTimingSegments[index] || [];
                return {
                    text: stepText,
                    timing_segments: segments.length > 0 ? segments : null
                };
            });
            submitData.append('steps_with_timing', JSON.stringify(stepsWithTiming));
            }

            submitData.append('existingImages', JSON.stringify(existingImages));

            Array.from(formData.images).forEach((image) => {
                submitData.append('images', image);
            });

            await updateRecipe(id, submitData, token);
            navigate(`/recipes/${id}`);
        } catch (err) {
            console.error('Error updating recipe:', err);
            setError('Failed to update recipe. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading recipe...</Text>
                    </div>
                </div>
            </div>
        );
    }

    const allImages = [...existingImages, ...imagePreviews];
    const currentImage = allImages[currentImageIndex];

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle="MODIFY RECIPE DETAILS"
                >
                    EDIT RECIPE
                </PageHeading>

                {/* Toast Error Messages */}
                {error && (
                    <Toast
                        message={error}
                        type="error"
                        onClose={() => setError('')}
                        duration={5000}
                    />
                )}
                {uploadError && (
                    <Toast
                        message={uploadError}
                        type="error"
                        onClose={() => setUploadError('')}
                        duration={5000}
                    />
                )}

                {/* Main Recipe Card - Unified Layout */}
                <div className="w-full flex justify-center px-4">
                    <div className="w-full max-w-[630px] md:max-w-[1000px] relative group">
                        <div className="bg-neutral-0 hairline p-6 relative z-10 transition-all duration-200 overflow-y-auto min-h-[95vh] sm:min-h-[600px] sm:max-h-[90vh]">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                            {/* Left Section - Text Content */}
                            <div className="h-full relative overflow-y-auto no-scrollbar">
                                <div className="space-y-6">
                                    {/* Recipe Header - Notecard Style */}
                                    <div className="mb-4">
                                        {/* Recipe Title - Typewriter font, bottom left aligned */}
                                        <div className="text-left mb-3">
                                            <div className="relative">
                                                {editingField === 'name' ? (
                                                    <input
                                                        type="text"
                                                        value={formData.name}
                                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        onBlur={stopEditing}
                                                        onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                                                        className="w-full text-4xl font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1"
                                                        style={{ color: colorTokens.oxfordBlue }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <h1 
                                                        className="text-4xl font-mono tracking-tight mb-2 cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                        style={{ color: colorTokens.oxfordBlue }}
                                                        onClick={() => startEditing('name')}
                                                    >
                                                        {formData.name || 'Click to add recipe name'}
                                                    </h1>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Red dividing line */}
                                        <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                        
                                        {/* Author info - centered */}
                                        <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                            <span>EDITING RECIPE</span>
                                            <span>•</span>
                                            <span>BY {user?.displayName || 'YOU'}</span>
                                        </div>
                                    </div>

                                    {/* Recipe Information */}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">TIME</div>
                                                <div className="relative">
                                                    {editingField === 'cooking_time' ? (
                                                        <input
                                                            type="number"
                                                            value={formData.cooking_time}
                                                            onChange={(e) => handleFieldChange('cooking_time', e.target.value)}
                                                            onBlur={stopEditing}
                                                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                                                            className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="font-mono text-lg cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                            onClick={() => startEditing('cooking_time')}
                                                        >
                                                            {formData.cooking_time ? formatCookingTime(formData.cooking_time) : 'Click to add time'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">CATEGORY</div>
                                                <div className="relative">
                                                    {editingField === 'category' ? (
                                                        <select
                                                            value={formData.category}
                                                            onChange={(e) => handleFieldChange('category', e.target.value)}
                                                            onBlur={stopEditing}
                                                            className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                            autoFocus
                                                        >
                                                            <option value="">Select category</option>
                                                            {categories.map(cat => (
                                                                <option key={cat} value={cat}>{cat}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <div 
                                                            className="font-mono text-lg cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                            onClick={() => startEditing('category')}
                                                        >
                                                            {formData.category || 'Click to select category'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">STATUS</div>
                                                <div className="font-mono text-lg">
                                                    EDITING
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Multi-Component Toggle */}
                                    <div className="bg-neutral-50 p-4 hairline">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">RECIPE TYPE</div>
                                                <Text className="text-sm opacity-60">
                                                    {formData.isMultiComponent 
                                                        ? 'Multi-component recipe with separate parts' 
                                                        : 'Simple recipe with a single list of ingredients and steps'}
                                                </Text>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={toggleMultiComponent}
                                                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                                                    formData.isMultiComponent
                                                        ? 'bg-neutral-900 text-neutral-0'
                                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                }`}
                                            >
                                                {formData.isMultiComponent ? 'MULTI-COMPONENT' : 'SIMPLE'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Conditional Rendering based on isMultiComponent */}
                                    {formData.isMultiComponent ? (
                                        <>
                                            {/* Multi-Component Mode */}
                                            <div>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900">COMPONENTS</div>
                                                    <button
                                                        type="button"
                                                        onClick={addComponent}
                                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                                    >
                                                        + ADD COMPONENT
                                                    </button>
                                                </div>
                                                <Text className="mb-4 opacity-60 text-sm">
                                                    Each component has its own ingredients, prerequisites, and steps.
                                                </Text>
                                                
                                                {formData.components.map((component, index) => (
                                                    <RecipeComponentEditor
                                                        key={component.id || index}
                                                        component={component}
                                                        onChange={(updated) => updateComponent(index, updated)}
                                                        onRemove={formData.components.length > 1 ? () => removeComponent(index) : null}
                                                        accentColor={index % 2 === 0 ? colorTokens.notecardBlue : colorTokens.oxfordBlue}
                                                    />
                                                ))}
                                            </div>

                                            {/* Section Separator */}
                                            <div className="border-t" style={{ borderColor: colorTokens.red }}></div>

                                            {/* Assembly Section */}
                                            <AssemblyEditor
                                                assembly={formData.assembly}
                                                onChange={updateAssembly}
                                                componentNames={formData.components.map(c => c.name).filter(Boolean)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {/* Simple Mode - Original UI */}
                                    {/* Ingredients */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INGREDIENTS</div>
                                        {formData.ingredients && formData.ingredients.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {formData.ingredients.map((ingredient, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                            {index + 1}.
                                                        </span>
                                                        <div className="flex-1">
                                                            {editingField === `ingredient_${index}` ? (
                                                                <textarea
                                                                    value={ingredient}
                                                                    onChange={(e) => handleListChange('ingredients', index, e.target.value)}
                                                                    onBlur={stopEditing}
                                                                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                    className="w-full text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                    style={{ 
                                                                        height: 'auto',
                                                                        minHeight: '20px'
                                                                    }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                    onClick={() => startEditing(`ingredient_${index}`)}
                                                                >
                                                                    {ingredient || 'Click to add ingredient'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {formData.ingredients.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeListItem('ingredients', index)}
                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => addListItem('ingredients')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD INGREDIENT
                                        </button>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Prerequisites */}
                                    {formData.prerequisites && formData.prerequisites.length > 0 && formData.prerequisites[0] && (
                                        <>
                                            <div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                                                <div className="space-y-3">
                                                    {formData.prerequisites.map((prerequisite, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                {index + 1}.
                                                            </span>
                                                            <div className="flex-1 flex items-center gap-2">
                                                                {editingField === `prerequisite_${index}` ? (
                                                                    <textarea
                                                                        value={prerequisite}
                                                                        onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                                                        onBlur={stopEditing}
                                                                        onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                        className="flex-1 text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                        style={{ 
                                                                            height: 'auto',
                                                                            minHeight: '20px'
                                                                        }}
                                                                        onInput={(e) => {
                                                                            e.target.style.height = 'auto';
                                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                                        }}
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="flex-1 text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                        onClick={() => startEditing(`prerequisite_${index}`)}
                                                                    >
                                                                        {prerequisite || 'Click to add prerequisite'}
                                                                    </div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeListItem('prerequisites', index)}
                                                                    className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors flex-shrink-0"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => addListItem('prerequisites')}
                                                    className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                                >
                                                    + ADD PREREQUISITE
                                                </button>
                                            </div>
                                            <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>
                                        </>
                                    )}

                                    {/* Instructions */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INSTRUCTIONS</div>
                                        {formData.steps && formData.steps.length > 0 ? (
                                            <div className="space-y-3">
                                                {formData.steps.map((step, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                            {index + 1}
                                                        </span>
                                                        <div className="flex-1">
                                                            {editingField === `step_${index}` ? (
                                                                <textarea
                                                                    value={step}
                                                                    onChange={(e) => handleListChange('steps', index, e.target.value)}
                                                                    onBlur={stopEditing}
                                                                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && stopEditing()}
                                                                    className="w-full text-sm bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 resize-none"
                                                                    style={{ 
                                                                        height: 'auto',
                                                                        minHeight: '20px'
                                                                    }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <div 
                                                                    className="text-sm cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                                                    onClick={() => startEditing(`step_${index}`)}
                                                                >
                                                                    {step || 'Click to add step'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {formData.steps.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeListItem('steps', index)}
                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                        {/* Active/Inactive Timer Display */}
                                                        <div className="flex items-center ml-2 h-8">
                                                            {(() => {
                                                                const segments = stepTimingSegments[index] || [];
                                                                const activeSegment = segments.find(s => s.kind === 'active');
                                                                const inactiveSegment = segments.find(s => s.kind === 'inactive');
                                                                const hasTimers = (activeSegment?.expectedSeconds || 0) > 0 || (inactiveSegment?.expectedSeconds || 0) > 0;
                                                                
                                                                if (hasTimers) {
                                                                    return (
                                                                        <div className="flex items-center gap-1">
                                                                            {activeSegment && activeSegment.expectedSeconds > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                                    style={{
                                                                                        backgroundColor: '#fee2e2',
                                                                                        border: '1px solid #fca5a5',
                                                                                        color: '#991b1b'
                                                                                    }}
                                                                                    title={activeSegment.label || 'Active time'}
                                                                                >
                                                                                    <TimerIcon size={14} className="mr-1" />
                                                                                    {Math.floor(activeSegment.expectedSeconds / 60)}m
                                                                                </button>
                                                                            )}
                                                                            {inactiveSegment && inactiveSegment.expectedSeconds > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                                    className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                                    style={{
                                                                                        backgroundColor: '#dbeafe',
                                                                                        border: '1px solid #93c5fd',
                                                                                        color: '#1e40af'
                                                                                    }}
                                                                                    title={inactiveSegment.label || 'Inactive time'}
                                                                                >
                                                                                    <TimerIcon size={14} className="mr-1" />
                                                                                    {Math.floor(inactiveSegment.expectedSeconds / 60)}m
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                // Show add timer icon for steps without timers
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        className="ml-2 flex items-center px-2 py-1 bg-neutral-50 text-neutral-600 hairline hover:bg-neutral-100 transition-colors h-8 text-xs font-mono"
                                                                        onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: [] })}
                                                                        title="Add timers"
                                                                    >
                                                                        <TimerIcon size={16} />
                                                                    </button>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-neutral-900 opacity-60 text-sm">No instructions provided.</div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => addListItem('steps')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD STEP
                                        </button>
                                    </div>
                                        </>
                                    )}
                                    {/* End of multi-component / simple recipe conditional */}

                                    {/* Fade effect overlay */}
                                    <div className="sticky bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-neutral-0 to-transparent pointer-events-none z-10"></div>
                                </div>
                            </div>

                            {/* Right Section - Image Management */}
                            <div className="h-full overflow-hidden h-[40vh] sm:h-full overflow-y-auto">
                                <div className="h-full flex flex-col">
                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">RECIPE IMAGES</div>
                                    
                                    {/* Image Grid */}
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {/* Image Placeholders/Previews */}
                                            {allImages.map((image, index) => (
                                                <div key={index} className="relative group aspect-square">
                                                    <div className="w-full h-full overflow-hidden hairline bg-neutral-50">
                                                        {index < existingImages.length ? (
                                                            <SignedImage
                                                                src={image}
                                                                alt={`Recipe image ${index + 1}`}
                                                                className="w-full h-full object-cover"
                                                                category={formData.category}
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={image} 
                                                                alt={`Preview ${index + 1}`} 
                                                                className="w-full h-full object-cover"
                                                            />
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (index < existingImages.length) {
                                                                handleRemoveExistingImage(image);
                                                            } else {
                                                                removeNewImage(index - existingImages.length);
                                                            }
                                                        }}
                                                        className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Remove image"
                                                    >
                                                        ✕
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            // Replace image at this index
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = async (e) => {
                                                                if (e.target.files && e.target.files.length > 0) {
                                                                    const file = e.target.files[0];
                                                                    // Validate file
                                                                    if (!file.type.startsWith('image/')) {
                                                                        setUploadError('Please select a valid image file');
                                                                        return;
                                                                    }
                                                                    if (file.size > 10 * 1024 * 1024) {
                                                                        // Try to resize
                                                                        try {
                                                                            const resizeImage = async (file) => {
                                                                                return new Promise((resolve) => {
                                                                                    const reader = new FileReader();
                                                                                    reader.onload = (e) => {
                                                                                        const img = new Image();
                                                                                        img.onload = () => {
                                                                                            const canvas = document.createElement('canvas');
                                                                                            const maxSize = 1920;
                                                                                            let width = img.width;
                                                                                            let height = img.height;
                                                                                            if (width > height) {
                                                                                                if (width > maxSize) {
                                                                                                    height *= maxSize / width;
                                                                                                    width = maxSize;
                                                                                                }
                                                                                            } else {
                                                                                                if (height > maxSize) {
                                                                                                    width *= maxSize / height;
                                                                                                    height = maxSize;
                                                                                                }
                                                                                            }
                                                                                            canvas.width = width;
                                                                                            canvas.height = height;
                                                                                            const ctx = canvas.getContext('2d');
                                                                                            ctx.drawImage(img, 0, 0, width, height);
                                                                                            canvas.toBlob(resolve, 'image/jpeg', 0.9);
                                                                                        };
                                                                                        img.src = e.target.result;
                                                                                    };
                                                                                    reader.readAsDataURL(file);
                                                                                });
                                                                            };
                                                                            const resizedFile = await resizeImage(file);
                                                                            if (index < existingImages.length) {
                                                                                // Replace existing image
                                                                                setExistingImages(prev => {
                                                                                    const newImages = [...prev];
                                                                                    newImages[index] = URL.createObjectURL(resizedFile);
                                                                                    return newImages;
                                                                                });
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    images: prev.images.map((img, i) => i === index ? resizedFile : img)
                                                                                }));
                                                                            } else {
                                                                                // Replace new image
                                                                                const previewIndex = index - existingImages.length;
                                                                                setImagePreviews(prev => {
                                                                                    const newPreviews = [...prev];
                                                                                    const reader = new FileReader();
                                                                                    reader.onload = (e) => {
                                                                                        newPreviews[previewIndex] = e.target.result;
                                                                                        setImagePreviews([...newPreviews]);
                                                                                    };
                                                                                    reader.readAsDataURL(resizedFile);
                                                                                    return newPreviews;
                                                                                });
                                                                                setFormData(prev => ({
                                                                                    ...prev,
                                                                                    images: prev.images.map((img, i) => i === previewIndex ? resizedFile : img)
                                                                                }));
                                                                            }
                                                                        } catch (err) {
                                                                            setUploadError('File is too large (max 10MB)');
                                                                        }
                                                                    } else {
                                                                        if (index < existingImages.length) {
                                                                            // Replace existing image
                                                                            setExistingImages(prev => {
                                                                                const newImages = [...prev];
                                                                                newImages[index] = URL.createObjectURL(file);
                                                                                return newImages;
                                                                            });
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                images: prev.images.map((img, i) => i === index ? file : img)
                                                                            }));
                                                                        } else {
                                                                            // Replace new image
                                                                            const previewIndex = index - existingImages.length;
                                                                            const reader = new FileReader();
                                                                            reader.onload = (e) => {
                                                                                setImagePreviews(prev => {
                                                                                    const newPreviews = [...prev];
                                                                                    newPreviews[previewIndex] = e.target.result;
                                                                                    return newPreviews;
                                                                                });
                                                                            };
                                                                            reader.readAsDataURL(file);
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                images: prev.images.map((img, i) => i === previewIndex ? file : img)
                                                                            }));
                                                                        }
                                                                    }
                                                                }
                                                            };
                                                            input.click();
                                                        }}
                                                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Replace image"
                                                    >
                                                        <span className="text-white font-mono text-xs uppercase tracking-wider">REPLACE</span>
                                                    </button>
                                                </div>
                                            ))}
                                            
                                            {/* Add Image Placeholder - Clickable Square */}
                                            {allImages.length < 10 && (
                                                <div 
                                                    className={`relative aspect-square hairline cursor-pointer transition-colors flex items-center justify-center group ${
                                                        dragOver ? 'bg-neutral-200' : 'bg-neutral-50 hover:bg-neutral-100'
                                                    }`}
                                                    onClick={triggerFileInput}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                >
                                                    <div className="text-center">
                                                        <div className="text-3xl mb-2 opacity-60 group-hover:opacity-80">+</div>
                                                        <div className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 group-hover:opacity-80">
                                                            ADD IMAGE
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Hidden File Input */}
                                        <input
                                            id="edit-image-input"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileInput}
                                            className="hidden"
                                        />
                                        
                                        {allImages.length > 0 && (
                                            <div className="mt-3 text-xs text-neutral-600 font-mono uppercase tracking-wider">
                                                Hover over images to edit or remove
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="max-w-5xl mx-auto mt-6">
                    <div className="flex justify-center items-center space-x-4">
                        <form onSubmit={handleSubmit} className="contents">
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`px-6 py-3 hairline font-mono text-sm uppercase tracking-wider transition-opacity ${
                                    submitting 
                                        ? 'bg-neutral-300 text-neutral-600 cursor-not-allowed' 
                                        : 'bg-neutral-900 text-neutral-0 hover:opacity-60'
                                }`}
                            >
                                {submitting ? 'UPDATING...' : 'UPDATE RECIPE'}
                            </button>
                        </form>
                        <button
                            type="button"
                            onClick={() => navigate(`/recipes/${id}`)}
                            disabled={submitting}
                            className={`px-6 py-3 hairline font-mono text-sm uppercase tracking-wider transition-colors ${
                                submitting 
                                    ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' 
                                    : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                            }`}
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
            <TimerEditModal
                open={timerModal.open}
                onClose={() => setTimerModal({ open: false })}
                onSave={(newSegments) => {
                    setStepTimingSegments(prev => ({
                        ...prev,
                        [timerModal.stepIndex]: newSegments
                    }));
                    setTimerModal({ open: false });
                }}
                onRemove={() => {
                    setStepTimingSegments(prev => {
                        const updated = { ...prev };
                        delete updated[timerModal.stepIndex];
                        return updated;
                    });
                    setTimerModal({ open: false });
                }}
                stepIndex={timerModal.stepIndex}
                timingSegments={timerModal.timingSegments}
            />
        </div>
    );
};

export default EditRecipe;
